import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Platform, ScrollView, Dimensions } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { translations as fullTranslations, Language } from '../config/translations';
import { Ionicons } from '@expo/vector-icons'; // Import Ionicons

// Helper to get user agent for better OS detection on web
const getOperatingSystem = () => {
  if (typeof window === 'undefined') return 'unknown'; // Not in browser
  const userAgent = window.navigator.userAgent || window.navigator.vendor || "";

  if (/android/i.test(userAgent)) {
    return "android";
  }

  // iOS detection
  // @ts-ignore iPadOS doesn't contain "iPad" on desktop mode anymore
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return "ios";
  }

  return "unknown"; // Default to unknown
};

const PwaInstallInstructionsScreen = () => {
  const [language, setLanguage] = useState<Language>('nl'); // Default to Dutch
  const [os, setOs] = useState<'ios' | 'android' | 'unknown'>('unknown');
  const [groupName, setGroupName] = useState<string | null>(null); // State for group name

  useEffect(() => {
    // Detect OS on mount (client-side)
    setOs(getOperatingSystem());

    // Load language preference
    const loadLang = async () => {
      const savedLang = await AsyncStorage.getItem('selectedLanguage') as Language;
      if (savedLang) {
        setLanguage(savedLang);
      }

      // Load pending group name if exists
      const storedGroupName = await AsyncStorage.getItem('pendingGroupName');
      if (storedGroupName) {
          console.log('Found pending group name for instructions:', storedGroupName);
          setGroupName(storedGroupName);
      }
    };
    loadLang();
  }, []);

  const t = fullTranslations[language]?.pwaInstructions || fullTranslations.nl.pwaInstructions;

  // Helper component for icons to avoid repetition
  const InstructionIcon = ({ name }: { name: keyof typeof Ionicons.glyphMap }) => (
    <View style={styles.iconContainer}>
        <Ionicons name={name} size={28} color="#005575" />
    </View>
  );

  const renderIOSInstructions = () => (
    <View style={styles.instructionSet}>
      <Text style={styles.osTitle}>{t.ios.title}</Text>
      <View style={styles.step}>
        <Text style={styles.stepNumber}>1.</Text>
        <Text style={styles.stepText}>{t.ios.step1}</Text>
      </View>
      <InstructionIcon name="share-outline" />
      <View style={styles.step}>
        <Text style={styles.stepNumber}>2.</Text>
        <Text style={styles.stepText}>{t.ios.step2}</Text>
      </View>
      <InstructionIcon name="add-circle-outline" />
      <View style={styles.step}>
        <Text style={styles.stepNumber}>3.</Text>
        <Text style={styles.stepText}>{t.ios.step3}</Text>
      </View>
    </View>
  );

  const renderAndroidInstructions = () => (
    <View style={styles.instructionSet}>
      <Text style={styles.osTitle}>{t.android.title}</Text>
      <View style={styles.step}>
        <Text style={styles.stepNumber}>1.</Text>
        <Text style={styles.stepText}>{t.android.step1}</Text>
      </View>
      <InstructionIcon name="ellipsis-vertical" />
      <View style={styles.step}>
        <Text style={styles.stepNumber}>2.</Text>
        <Text style={styles.stepText}>{t.android.step2}</Text>
      </View>
      <InstructionIcon name="download-outline" />
      <View style={styles.step}>
        <Text style={styles.stepNumber}>3.</Text>
        <Text style={styles.stepText}>{t.android.step3}</Text>
      </View>
    </View>
  );

  const renderUnknownInstructions = () => (
      <View style={styles.instructionSet}>
          <Text style={styles.osTitle}>{t.unknown.title}</Text>
          <Text style={styles.stepText}>{t.unknown.message}</Text>
      </View>
  );

  return (
    // Use a View container for better background control if needed
    <View style={styles.outerContainer}>
        <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
            <Ionicons name="download-outline" size={48} color="#005575" style={styles.mainIcon} />
            <Text style={styles.mainTitle}>
                {groupName ? `${t.mainTitle} (${groupName})` : t.mainTitle}
            </Text>
            <Text style={styles.subTitle}>{t.subTitle}</Text>
            
            {os === 'ios' && renderIOSInstructions()}
            {os === 'android' && renderAndroidInstructions()}
            {os === 'unknown' && renderUnknownInstructions()}

            <Text style={styles.footerText}>{t.footer}</Text>
        </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF', // Use white as the main background
  },
  container: {
    flex: 1,
  },
  contentContainer: {
    paddingVertical: 40, // More vertical padding
    paddingHorizontal: 20, // Standard horizontal padding
    alignItems: 'center',
    minHeight: Dimensions.get('window').height, 
    justifyContent: 'center',
  },
  mainIcon: {
      marginBottom: 15,
  },
  mainTitle: {
    fontSize: 24, // Slightly smaller title
    fontWeight: 'bold',
    color: '#003f5a', // Darker shade of the blue
    textAlign: 'center',
    marginBottom: 10,
  },
  subTitle: {
    fontSize: 16,
    color: '#555', // Darker grey for better readability
    textAlign: 'center',
    marginBottom: 35, // More space before instructions
    lineHeight: 23,
    maxWidth: 500,
  },
  instructionSet: {
    backgroundColor: '#f8f9fa', // Lighter background for the card
    borderRadius: 10, // More rounded corners
    padding: 25, // More padding inside the card
    marginBottom: 30,
    width: '100%',
    maxWidth: 450, // Limit width on larger screens
    borderWidth: 1,
    borderColor: '#e9ecef', // Subtle border
  },
  osTitle: {
    fontSize: 18, // Slightly smaller OS title
    fontWeight: 'bold',
    color: '#005575', 
    marginBottom: 20, // More space after title
    paddingBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#dee2e6', // Clearer border
  },
  step: {
    flexDirection: 'row',
    marginBottom: 15, // More space between steps
    alignItems: 'center', // Align items vertically centered
  },
  stepNumber: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#005575',
    marginRight: 12, // More space after number
    minWidth: 20, 
  },
  stepText: {
    fontSize: 15, // Slightly smaller text for steps
    color: '#343a40', // Darker text color
    flex: 1, 
    lineHeight: 21,
  },
  iconContainer: { // Style for the icon wrapper
    alignItems: 'center', // Center the icon
    marginVertical: 15, // Space around the icon
    padding: 10,
    backgroundColor: '#e9ecef', // Light background for icon container
    borderRadius: 8,
    alignSelf: 'center',
    minWidth: 60, // Give it some minimum width
  },
  footerText: {
      fontSize: 14,
      color: '#6c757d', // Softer grey for footer
      textAlign: 'center',
      marginTop: 15, // More space before footer
      lineHeight: 20,
  }
});

export default PwaInstallInstructionsScreen; 